import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X, Plus, Star, SearchX, MapPinned, RefreshCw } from 'lucide-react';
import { useLieux } from '../hooks/useLieux';
import { rechercher, getType, TYPES } from '../lib/lieux';
import { LieuCard, LieuDetail } from '../components/Lieu';
import QRModal from '../components/QRModal';
import { Sheet, SkeletonListe, useDesktop, useToast } from '../components/ui';

const PAGE = 30; // pagination progressive

const TITRES = {
  tous: 'Mes lieux',
  recherche: 'Rechercher',
  favoris: 'Mes favoris',
};

export default function MesLieux({ mode = 'tous' }) {
  const navigate = useNavigate();
  const toast = useToast();
  const desktop = useDesktop();
  const [params, setParams] = useSearchParams();
  const { lieux, indexes, parId, loading, error, charger, basculerFavori, marquerConsulte } = useLieux();

  const [q, setQ] = useState(params.get('q') || '');
  const filtre = mode === 'favoris' ? 'favoris' : (params.get('type') || 'tous');
  const [selection, setSelection] = useState(params.get('id') || null);
  const [qrLieu, setQrLieu] = useState(null);
  const [limite, setLimite] = useState(PAGE);
  const champ = useRef(null);

  useEffect(() => { if (mode === 'recherche' && champ.current) champ.current.focus(); }, [mode]);
  useEffect(() => { setLimite(PAGE); }, [q, filtre]);

  // Garder la recherche dans l'URL (retour arrière, partage d'écran)
  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams(params);
      if (q) p.set('q', q); else p.delete('q');
      if (p.toString() !== params.toString()) setParams(p, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const choisirFiltre = (f) => {
    const p = new URLSearchParams(params);
    if (f === 'tous') p.delete('type'); else p.set('type', f);
    setParams(p, { replace: true });
  };

  const resultats = useMemo(() => {
    let base = indexes;
    if (filtre === 'favoris') base = base.filter(x => x.lieu.favori);
    else if (filtre !== 'tous') base = base.filter(x => x.lieu.type === filtre);
    return rechercher(base, q);
  }, [indexes, filtre, q]);

  // Filtres affichés : seulement les types réellement utilisés
  const typesUtilises = useMemo(() => {
    const presents = new Set(lieux.map(l => l.type));
    return TYPES.filter(t => presents.has(t.id));
  }, [lieux]);

  const ouvrir = useCallback((lieu) => {
    setSelection(lieu.id);
    marquerConsulte(lieu.id);
  }, [marquerConsulte]);

  const fermer = useCallback(() => setSelection(null), []);

  const surFavori = useCallback(async (lieu) => {
    const r = await basculerFavori(lieu);
    if (r.error) toast(r.error, 'erreur');
    else toast(lieu.favori ? 'Retiré des favoris' : 'Ajouté aux favoris');
  }, [basculerFavori, toast]);

  const lieuSelectionne = selection ? parId[selection] : null;

  // Sur desktop, sélectionner le premier résultat pour éviter un panneau vide
  useEffect(() => {
    if (desktop && !lieuSelectionne && resultats.length > 0) setSelection(resultats[0].id);
  }, [desktop, lieuSelectionne, resultats]);

  const vide = !loading && !error && lieux.length === 0;

  return (
    <div className="page page-large">
      <div className="page-tete">
        <div>
          <h1>{TITRES[mode]}</h1>
          {!loading && (
            <p className="texte-secondaire">
              {mode === 'favoris'
                ? `${resultats.length} favori${resultats.length > 1 ? 's' : ''}`
                : `${lieux.length} lieu${lieux.length > 1 ? 'x' : ''} enregistré${lieux.length > 1 ? 's' : ''}`}
            </p>
          )}
        </div>
      </div>

      {!vide && (
        <div className="recherche">
          <Search size={20} className="recherche-icone" aria-hidden="true" />
          <input
            ref={champ}
            type="search"
            inputMode="search"
            placeholder="Rechercher un lieu, un nom ou un CAU…"
            value={q}
            onChange={e => setQ(e.target.value)}
            aria-label="Rechercher un lieu"
            autoComplete="off"
          />
          {q && <button className="btn-icone" onClick={() => setQ('')} aria-label="Effacer"><X size={18} /></button>}
        </div>
      )}

      {mode !== 'favoris' && typesUtilises.length > 0 && (
        <div className="filtres" role="tablist" aria-label="Filtrer par type">
          <button role="tab" aria-selected={filtre === 'tous'} className={`chip ${filtre === 'tous' ? 'actif' : ''}`} onClick={() => choisirFiltre('tous')}>Tous</button>
          <button role="tab" aria-selected={filtre === 'favoris'} className={`chip ${filtre === 'favoris' ? 'actif' : ''}`} onClick={() => choisirFiltre('favoris')}>
            <Star size={14} /> Favoris
          </button>
          {typesUtilises.map(t => (
            <button key={t.id} role="tab" aria-selected={filtre === t.id} className={`chip ${filtre === t.id ? 'actif' : ''}`} onClick={() => choisirFiltre(t.id)}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="etat">
          <p>{error}</p>
          <button className="btn btn-secondaire" onClick={charger}><RefreshCw size={16} /> Réessayer</button>
        </div>
      )}

      {vide && (
        <div className="etat">
          <span className="etat-icone"><MapPinned size={32} /></span>
          <h2>Aucun lieu enregistré</h2>
          <p className="texte-secondaire">Enregistrez votre maison, votre bureau ou un client pour les retrouver en un instant.</p>
          <button className="btn btn-primaire btn-large" onClick={() => navigate('/lieux/nouveau')}><Plus size={18} /> Ajouter mon premier lieu</button>
        </div>
      )}

      {loading ? <SkeletonListe n={3} /> : !vide && !error && (
        <div className="liste-detail">
          <div>
            {resultats.length === 0 ? (
              <div className="etat">
                <span className="etat-icone">{filtre === 'favoris' ? <Star size={30} /> : <SearchX size={30} />}</span>
                {filtre === 'favoris' && !q ? (
                  <>
                    <h2>Aucun favori</h2>
                    <p className="texte-secondaire">Touchez l'étoile d'un lieu pour le retrouver ici.</p>
                  </>
                ) : (
                  <>
                    <h2>Aucun résultat</h2>
                    <p className="texte-secondaire">
                      {q ? <>Aucun lieu ne correspond à « {q} »{filtre !== 'tous' ? ` dans ${filtre === 'favoris' ? 'vos favoris' : getType(filtre).label}` : ''}.</> : 'Aucun lieu de ce type.'}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="liste-lieux">
                {resultats.slice(0, limite).map(l => (
                  <LieuCard key={l.id} lieu={l} actif={desktop && l.id === selection}
                    onOuvrir={ouvrir} onFavori={surFavori} onQR={setQrLieu} />
                ))}
                {resultats.length > limite && (
                  <button className="btn btn-secondaire btn-large" onClick={() => setLimite(x => x + PAGE)}>
                    Afficher plus ({resultats.length - limite})
                  </button>
                )}
              </div>
            )}
          </div>

          {desktop && (
            <aside className="panneau-detail">
              {lieuSelectionne ? <LieuDetail lieu={lieuSelectionne} onFerme={fermer} /> : null}
            </aside>
          )}
        </div>
      )}

      {!desktop && (
        <Sheet open={!!lieuSelectionne} onClose={fermer} label="Détail du lieu">
          <LieuDetail lieu={lieuSelectionne} onFerme={fermer} />
        </Sheet>
      )}

      <QRModal lieu={qrLieu} onClose={() => setQrLieu(null)} />
    </div>
  );
}
